import { ability, ACTIONS, PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { RoleCategory } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';
import { useCombinedQueriesTeamGeneralInfo } from './query/useCombinedQueriesTeamGeneralInfo';
import { useQueryUser } from './query/useQueryUser';

/**
 * Returns the list of team clinicians the current user can create sessions for.
 *
 * Used by the delegation roles (Assistant, Admin) that create sessions on
 * behalf of others — the list itself is role-agnostic.
 *
 * - Filters `joined_team_members` from the cached team query (no extra API calls)
 * - Excludes support-only role members
 * - Includes the current user ("Me") only if they have SESSION_CREATE permission
 */
export const useTeamClinicianList = () => {
  const { teamDetails, isTeamDataReady } = useCombinedQueriesTeamGeneralInfo();
  const { data: kindeUserId } = useQueryUser((data) => data?.kinde_user_id);

  const canCreateForSelf = ability.can(
    ACTIONS.ACCESS,
    PERMISSION_SUBJECTS.SESSION_CREATE
  );

  const clinicians = useMemo(() => {
    const acceptedUsers = teamDetails?.team.joined_team_members;
    if (!acceptedUsers) return [];

    return acceptedUsers.filter((user) => {
      if (user.kinde_user_id === kindeUserId && !canCreateForSelf) {
        return false;
      }

      const isSupportOnly =
        user.user_role_list?.length === 1 &&
        user.user_role_list.at(0)?.category === RoleCategory.Support;
      if (isSupportOnly) {
        return false;
      }

      return true;
    });
  }, [teamDetails?.team.joined_team_members, kindeUserId, canCreateForSelf]);

  // `isTeamDataReady` (not `isLoadingTeamDetails`) so the loading state also covers
  // the joined-teams fetch. The team-details query is `enabled: !!teamId`, so while
  // joined-teams is still in flight it stays disabled — pending/idle, `isLoading`
  // false — which would otherwise flash the empty state before "Loading...".
  return { clinicians, isLoading: !isTeamDataReady, currentUserId: kindeUserId };
};
